<template>
  <div class="file-badges">
    <div class="flex flex-wrap">
      <div
        v-for="fileRelation in modelValue"
        :key="fileRelation.id"
        @click.stop="onClickFileRelation(fileRelation)"
        @dragstart="onDragStart($event, fileRelation)"
        @dragend="onDragEnd"
        draggable="true"
      >
        <PBadge
          class="mt-1 mr-1"
          :class="selected === fileRelation ? 'bg-primary dark:bg-primary-dark ring-2' : ''"
        >
          {{ fileRelation.file.originalName }}
        </PBadge>
      </div>
      <PProgress
        as="span"
        size="sm"
        aria-label="Bestand wordt geüpload"
        class="mt-0.5 ml-1 w-16! shrink-0"
        v-if="showUploadingState"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia"
import PBadge from "@/components/elements/PBadge.vue"
import { FileRelation } from "@/models/file/file-relation.model"
import PProgress from "@/components/elements/PProgress.vue"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import { useDragStore } from "@/store/drag-store"
import { FileHelper } from "@/helper/file-helper.ts"

const props = defineProps({
  /**
   * While true, shows progress to indicate a file being uploaded.
   */
  showUploadingState: {
    type: Boolean,
    default: false
  },
  /**
   * If set, this function will be called when a viewable file is clicked.
   * The function receives the file relation that was clicked.
   */
  customFileViewerHandler: {
    type: Function,
    default: null
  },
  selected: {
    type: Object as () => FileRelation,
    default: null
  },
  /**
   * If true, dragging files will copy them instead of moving them.
   */
  dragDefaultIsCopy: {
    type: Boolean,
    default: false
  }
})

defineEmits(["showUploadingState"])

const modelValue = defineModel<FileRelation[]>({ required: true })

const dragStore = useDragStore()
const { dragData } = storeToRefs(dragStore)

function onClickFileRelation(fileRelation: FileRelation) {
  if (FileHelper.isSupportedFile(fileRelation.file)) {
    if (props.customFileViewerHandler) {
      props.customFileViewerHandler(modelValue, fileRelation)
    } else {
      FileViewerController.display(modelValue.value, fileRelation)
    }
  } else {
    // use window open to prevent card from collapsing
    window.open(fileRelation.file.url, "_blank")
  }
}

function onDragStart(e: any, fileRelation: FileRelation) {
  e.dataTransfer.setData("fileRelationUuid", fileRelation.id)

  const isMac = navigator.userAgent.indexOf("Mac") > -1
  const isWin = navigator.userAgent.indexOf("Win") > -1
  const isCopyOperation = props.dragDefaultIsCopy || (isWin && e.ctrlKey) || (isMac && e.altKey)
  e.dataTransfer.setData("intention", isCopyOperation ? "copy" : "move")
  dragData.value = fileRelation
}

function onDragEnd() {
  dragData.value = null
}
</script>

<style scoped></style>
